import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";

import { DpEmployeeListItem, listEmployeesApi } from "../api/employeesApi";
import { bulkAssignStoreApi, BulkStoreAssignmentResult, unassignStoreApi } from "../api/scheduleApi";
import { formatCpf } from "../utils/format";
import EcoConfirmDialog from "../../../components/eco-ui/EcoConfirmDialog";

type Props = {
  token: string;
  storeId: string;
  storeName: string;
  onClose: () => void;
  onDone: () => void;
};

/** sem-loja e todos: lotar nesta loja. nesta-loja: retirar desta loja. */
type Source = "sem-loja" | "nesta-loja" | "todos";

/**
 * Lotar vários colaboradores numa loja, ou retirá-los dela, de uma vez. Vale na
 * hora, sem data (a loja é um vínculo atual do colaborador). Quem está em outra
 * loja é transferido; o backend pula, com o motivo, quem não pode.
 */
export default function BulkStoreAssignmentModal({ token, storeId, storeName, onClose, onDone }: Props) {
  const [source, setSource] = useState<Source>("sem-loja");
  const [search, setSearch] = useState("");
  const [items, setItems] = useState<DpEmployeeListItem[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState<BulkStoreAssignmentResult | null>(null);
  const [lastAction, setLastAction] = useState<"lotar" | "retirar">("lotar");
  const [confirmRemove, setConfirmRemove] = useState(false);

  const removing = source === "nesta-loja";

  const changeSource = (next: Source) => {
    setSource(next);
    setSelected(new Set());
  };

  useEffect(() => {
    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const page = await listEmployeesApi(token, {
          q: search.trim() || undefined,
          limit: 100,
          ...(source === "sem-loja" ? { withoutStore: "true" as const } : {}),
          ...(source === "nesta-loja" ? { storeId } : {}),
        });
        setItems(page.items.filter((e) => e.status !== "DESLIGADO"));
      } catch (error: any) {
        toast.error(error?.message || "Erro ao buscar colaboradores.");
      } finally {
        setLoading(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [token, search, source, storeId]);

  /** Ao lotar, quem já está nesta loja não é escolhível; ao retirar, todos da lista são. */
  const isSelectable = (e: DpEmployeeListItem) => (removing ? true : e.store?.id !== storeId);
  const selectable = useMemo(() => items.filter(isSelectable), [items, removing, storeId]); // eslint-disable-line react-hooks/exhaustive-deps
  const names = useMemo(() => new Map(items.map((e) => [e.id, e.fullName])), [items]);

  const toggle = (id: string) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelected(next);
  };

  const allChecked = selectable.length > 0 && selectable.every((e) => selected.has(e.id));
  const toggleAll = () => {
    const next = new Set(selected);
    if (allChecked) selectable.forEach((e) => next.delete(e.id));
    else selectable.forEach((e) => next.add(e.id));
    setSelected(next);
  };

  const submit = async (confirmed = false) => {
    if (selected.size === 0) return toast.error("Escolha ao menos um colaborador.");
    if (removing && !confirmed) return setConfirmRemove(true);
    setConfirmRemove(false);
    setSaving(true);
    try {
      const payload = { storeId, employeeIds: Array.from(selected) };
      const res = removing ? await unassignStoreApi(token, payload) : await bulkAssignStoreApi(token, payload);
      setLastAction(removing ? "retirar" : "lotar");
      setResult(res);
      if (res.assigned.length) {
        toast.success(
          removing
            ? `${res.assigned.length} colaborador(es) retirado(s) de ${storeName}.`
            : `${res.assigned.length} colaborador(es) lotado(s) em ${storeName}.`,
        );
      }
      if (!res.skipped.length) onDone();
    } catch (error: any) {
      toast.error(error?.message || "Não foi possível concluir.");
    } finally {
      setSaving(false);
    }
  };

  const emptyText = source === "sem-loja" ? "Ninguém sem loja." : source === "nesta-loja" ? "Ninguém lotado nesta loja." : "Nenhum colaborador encontrado.";

  return (
    <div className="dpModalOverlay">
      <div className="dpModal dpModal--wide">
        <header className="dpModal__header">
          <h2 className="dpModal__title">Colaboradores da loja — {storeName}</h2>
          <button className="dpModal__close" onClick={result ? onDone : onClose}>
            &times;
          </button>
        </header>
        <div className="dpModal__body">
          {result ? (
            <>
              <p className="dpNotice">
                {result.assigned.length} {lastAction === "retirar" ? "retirado(s)" : "lotado(s)"}. {result.skipped.length} não{" "}
                {lastAction === "retirar" ? "retirado(s)" : "lotado(s)"}:
              </p>
              <ul className="dpRuleList">
                {result.skipped.map((s) => (
                  <li key={s.employeeId}>
                    <strong>{names.get(s.employeeId) ?? s.employeeId}</strong>: {s.reason}
                  </li>
                ))}
              </ul>
            </>
          ) : (
            <>
              <div className="dpFormGrid">
                <div className="dpFormGroup">
                  <label htmlFor="assignSource">Mostrar</label>
                  <select id="assignSource" className="dpSelect" value={source} onChange={(e) => changeSource(e.target.value as Source)}>
                    <option value="sem-loja">Só quem está sem loja</option>
                    <option value="nesta-loja">Somente nesta loja (para retirar)</option>
                    <option value="todos">Todos (inclui transferência de outra loja)</option>
                  </select>
                </div>
                <div className="dpFormGroup" style={{ gridColumn: "1 / -1" }}>
                  <label htmlFor="assignSearch">Buscar</label>
                  <input id="assignSearch" className="dpInput" placeholder="Nome ou CPF" value={search} onChange={(e) => setSearch(e.target.value)} />
                </div>
              </div>


              <div className="dpTableWrapper" style={{ maxHeight: 360, overflowY: "auto" }}>
                <table className="dpTable">
                  <thead>
                    <tr>
                      <th style={{ width: 40 }}>
                        <input type="checkbox" aria-label="Selecionar todos" checked={allChecked} onChange={toggleAll} disabled={!selectable.length} />
                      </th>
                      <th>Colaborador</th>
                      <th>CPF</th>
                      <th>Loja atual</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr>
                        <td colSpan={4} className="dpEmpty">
                          Carregando...
                        </td>
                      </tr>
                    ) : items.length === 0 ? (
                      <tr>
                        <td colSpan={4} className="dpEmpty">
                          {emptyText}
                        </td>
                      </tr>
                    ) : (
                      items.map((e) => {
                        const enabled = isSelectable(e);
                        const here = e.store?.id === storeId;
                        return (
                          <tr key={e.id} style={enabled ? undefined : { opacity: 0.55 }}>
                            <td>
                              <input
                                type="checkbox"
                                aria-label={`Selecionar ${e.fullName}`}
                                checked={selected.has(e.id)}
                                onChange={() => toggle(e.id)}
                                disabled={!enabled}
                              />
                            </td>
                            <td>{e.fullName}</td>
                            <td>{formatCpf(e.cpf)}</td>
                            <td>
                              {here ? (removing ? storeName : "Já está nesta loja") : e.store?.nomeFantasia ?? "Sem loja"}
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
              {items.length === 100 ? <p className="dpFormHint">Mostrando os 100 primeiros: use a busca para achar os demais.</p> : null}
            </>
          )}
        </div>
        <footer className="dpModal__footer">
          {result ? (
            <button className="dpBtn dpBtn--primary" onClick={onDone}>
              Fechar
            </button>
          ) : (
            <>
              <button className="dpBtn dpBtn--secondary" onClick={onClose} disabled={saving}>
                Cancelar
              </button>
              {removing ? (
                <button className="dpBtn dpBtn--danger" onClick={() => submit()} disabled={saving || selected.size === 0}>
                  Retirar {selected.size || ""} da loja
                </button>
              ) : (
                <button className="dpBtn dpBtn--primary" onClick={() => submit()} disabled={saving || selected.size === 0}>
                  Lotar {selected.size || ""} colaborador(es)
                </button>
              )}
            </>
          )}
        </footer>
      </div>
      <EcoConfirmDialog
        open={confirmRemove}
        title="Retirar da loja?"
        message={
          <>
            <strong>{selected.size} colaborador(es)</strong> deixam de estar em <strong>{storeName}</strong> e saem da escala dela na
            próxima sugestão.
          </>
        }
        confirmLabel="Retirar da loja"
        tone="danger"
        busy={saving}
        onConfirm={() => submit(true)}
        onCancel={() => setConfirmRemove(false)}
      />
    </div>
  );
}
